import React, { useState } from 'react';
import ConversationList from './ConversationList';
import UserProfile from './UserProfile';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ModelSelector from './ModelSelector';

function ChatPage({
  user,
  conversations,
  currentConversation,
  messages,
  input,
  setInput,
  isLoading,
  showModelSelector,
  availableModels,
  selectedModel,
  isPrivate,
  backendError,
  messagesEndRef,
  handleLogout,
  createNewConversation,
  loadConversation,
  deleteConversation,
  toggleModelSelector,
  togglePrivateMode,
  changeModel,
  sendMessage,
  stopGeneration
}) {
  // State pentru conversația cu meniul contextual deschis
  const [contextMenuConversation, setContextMenuConversation] = useState(null);
  // State pentru a ascunde/afișa bara de conversații
  const [isSidebarVisible, setSidebarVisible] = useState(true);
  // State pentru mesajul de avertizare
  const [warningMessage, setWarningMessage] = useState('');
  // State pentru căutare în conversații
  const [searchTerm, setSearchTerm] = useState('');

  // Gestionare click dreapta pe conversație
  const handleContextMenu = (e, conversationId) => {
    e.preventDefault(); // Previne meniul contextual normal al browser-ului
    setContextMenuConversation(conversationId);
  };

  // Închide meniul contextual când se face click în altă parte
  const handleOutsideClick = () => {
    setContextMenuConversation(null);
  };

  // Toggle pentru bara laterală
  const toggleSidebar = () => {
    setSidebarVisible(!isSidebarVisible);
  };

  // Funcție pentru a gestiona crearea unei conversații noi
  const handleCreateNewConversation = async () => {
    // Verificăm dacă avem o conversație curentă goală
    const hasEmptyConversation = currentConversation && 
                              messages.length === 0 && 
                              conversations.some(conv => 
                                conv.id === currentConversation && 
                                (!conv.title || conv.title === 'Conversație nouă'));
    
    if (hasEmptyConversation) {
      // Afișăm un mesaj de avertizare
      setWarningMessage('Utilizați conversația curentă goală înainte de a crea una nouă');
      
      // Ascundem mesajul după 3 secunde
      setTimeout(() => {
        setWarningMessage('');
      }, 3000);
      
      return;
    }
    
    // Dacă nu există o conversație goală, creăm una nouă
    setWarningMessage('');
    await createNewConversation();
  };

  // Filtrarea conversațiilor în funcție de termenul de căutare
  const filteredConversations = conversations.filter(conversation => {
    const title = conversation.title || 'Conversație nouă';
    return title.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className={`flex flex-col h-screen ${isPrivate ? 'bg-black' : 'bg-gray-100 dark:bg-gray-900'} overflow-hidden`} onClick={handleOutsideClick}>
      <div className="flex flex-1 overflow-hidden">
        {/* Bara de conversații colapsabilă */}
        <div className={`transition-all duration-300 ${isSidebarVisible ? 'w-64' : 'w-0'} flex flex-col h-full bg-gray-200 dark:bg-gray-800 border-r border-gray-300 dark:border-gray-700 overflow-hidden`}>
          {/* Header cu titlu și buton pentru toggle */}
          <div className="px-4 py-3 border-b border-gray-300 dark:border-gray-700 bg-gray-300 dark:bg-gray-700 flex items-center justify-between">
            <div className="text-gray-800 dark:text-gray-200 font-medium">
              Conversații
            </div>
            <button 
              onClick={toggleSidebar}
              className="p-1 rounded-md hover:bg-gray-400 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 focus:outline-none"
              title="Ascunde bara laterală"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
              </svg>
            </button>
          </div>
          
          {/* Input de căutare */}
          <div className="px-4 py-2 border-b border-gray-300 dark:border-gray-700">
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-2">
                <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              </span>
              <input
                type="text"
                placeholder="Caută conversații..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full py-2 pl-10 pr-3 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  className="absolute inset-y-0 right-0 flex items-center pr-2"
                  onClick={() => setSearchTerm('')}
                  title="Șterge căutarea"
                >
                  <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          
          {/* Buton conversație nouă și mesaj de avertizare */}
          <div className="px-4 py-3 border-b border-gray-300 dark:border-gray-700">
            {warningMessage && (
              <div className="mb-2 p-2 bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100 text-sm rounded">
                {warningMessage}
              </div>
            )}
            <button 
              onClick={handleCreateNewConversation}
              className="w-full py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Conversație nouă
            </button>
          </div>
          
          {/* Lista conversațiilor - acum are flex-grow și overflow-y-auto pentru scroll */}
          <div className="flex-grow overflow-y-auto">
            {filteredConversations.length > 0 ? (
              <ul className="space-y-1 py-2">
                {filteredConversations.map((conversation) => (
                  <li 
                    key={conversation.id}
                    className={`px-4 py-2 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-700 flex justify-between items-center ${
                      currentConversation === conversation.id 
                        ? 'bg-gray-300 dark:bg-gray-700' 
                        : ''
                    } relative`}
                    onClick={() => loadConversation(conversation.id)}
                    onContextMenu={(e) => handleContextMenu(e, conversation.id)}
                  >
                    <span className={`truncate ${
                      currentConversation === conversation.id 
                        ? 'text-blue-600 dark:text-blue-400 font-medium' 
                        : 'text-gray-800 dark:text-gray-200'
                    }`}>
                      {conversation.title || 'Conversație nouă'}
                    </span>
                    
                    {/* Afișăm butonul de ștergere doar dacă această conversație are meniul contextual deschis */}
                    {contextMenuConversation === conversation.id && (
                      <div 
                        className="absolute right-4 top-full mt-1 bg-white dark:bg-gray-800 shadow-lg rounded z-10 border border-gray-200 dark:border-gray-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteConversation(conversation.id);
                            setContextMenuConversation(null);
                          }}
                          className="flex items-center px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4 0a1 1 0 112 0v6a1 1 0 11-2 0V8z" clipRule="evenodd" />
                          </svg>
                          Șterge conversația
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : searchTerm ? (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                Nicio conversație găsită pentru "{searchTerm}"
              </div>
            ) : (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                Nicio conversație
              </div>
            )}
          </div>
          
          {/* Profilul utilizatorului - acum este mereu la baza sidebarului */}
          <UserProfile
            username={user?.username}
            onLogout={handleLogout}
          />
        </div>

        {/* Zona principală de chat */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header zona chat - cu buton pentru afișare sidebar când este ascuns */}
          <div className="px-3 py-2 bg-white dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700 flex items-center">
            {!isSidebarVisible && (
              <button 
                onClick={toggleSidebar} 
                className="p-2 mr-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                title="Arată conversațiile"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </svg>
              </button>
            )}
            <h2 className="text-lg font-medium text-gray-800 dark:text-gray-200">
              {conversations?.find(conv => conv.id === currentConversation)?.title || 'Chat'}
            </h2>
          </div>
          
          {/* Mesajele */}
          <MessageList
            messages={messages}
            messagesEndRef={messagesEndRef}
            backendError={backendError}
            isPrivate={isPrivate}
          />
          
          {/* Input pentru mesaj nou */}
          <div className={`border-t border-gray-300 dark:border-gray-700 p-4 ${
            isPrivate ? 'bg-black text-white' : 'bg-white dark:bg-gray-800'
          }`}>
            <div className="flex gap-2 mb-3">
              <ModelSelector
                selectedModel={selectedModel}
                availableModels={availableModels}
                showModelSelector={showModelSelector}
                onToggleSelector={toggleModelSelector}
                onChangeModel={changeModel}
              />
              
              {/* Buton pentru conversații private */}
              <button
                onClick={() => {
                  console.log(`DEBUG - Apăsat buton private. Stare curentă: ${isPrivate}, se va schimba în: ${!isPrivate}`);
                  togglePrivateMode();
                }}
                className={`px-3 py-2 rounded text-sm flex items-center gap-2 ${
                  isPrivate 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
                title={isPrivate ? 'Dezactivează modul privat' : 'Activează modul privat'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <span>{isPrivate ? 'Privat' : 'Public'}</span>
              </button>
            </div>
            
            <MessageInput
              input={input}
              setInput={setInput}
              sendMessage={sendMessage}
              isLoading={isLoading}
              stopGeneration={stopGeneration}
              isPrivate={isPrivate}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatPage; 